import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

interface TreatmentPlanData {
  id: string;
  title: string;
  status: string;
  treatment_type: string;
  doctor_id: string;
  patient_id: string;
  created_at: string;
  date: string; // This is a derived field, not from the DB
  description?: string;
  estimated_cost?: number;
}

interface PatientTreatmentPlansProps {
  patientId: string;
}

const PatientTreatmentPlans: React.FC<PatientTreatmentPlansProps> = ({ patientId }) => {
  const [plans, setPlans] = useState<TreatmentPlanData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTreatmentPlans = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('treatment_plans')
          .select('*')
          .eq('patient_id', patientId)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        // Transform data to include formatted date
        const formattedPlans = data.map(plan => ({
          ...plan,
          date: format(new Date(plan.created_at), 'MMM dd, yyyy')
        }));
        
        setPlans(formattedPlans);
      } catch (error: any) {
        console.error('Error fetching treatment plans:', error.message);
        toast.error('Failed to load treatment plans');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (patientId) {
      fetchTreatmentPlans();
    }
  }, [patientId]);

  const renderPlanCard = (plan: TreatmentPlanData) => {
    return (
      <Card key={plan.id} className="mb-4">
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold">{plan.title}</h3>
              <p className="text-sm text-muted-foreground">{plan.date}</p>
            </div>
            <div className="flex items-center">
              <span 
                className={`text-xs px-2 py-1 rounded-full mr-2 ${
                  plan.status === 'completed' ? 'bg-green-100 text-green-800' :
                  plan.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  plan.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  plan.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}
              >
                {plan.status.replace('_', ' ').charAt(0).toUpperCase() + plan.status.replace('_', ' ').slice(1)}
              </span>
              <Link to={`/treatment-plans/${plan.id}`}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
          {plan.description && (
            <p className="text-sm mt-2 line-clamp-2">{plan.description}</p>
          )}
          <div className="flex justify-between mt-2">
            <span className="text-sm">Type: <span className="font-medium capitalize">{plan.treatment_type}</span></span>
            {plan.estimated_cost && (
              <span className="text-sm">
                Est. Cost: <span className="font-medium">${plan.estimated_cost.toLocaleString()}</span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const activePlans = plans.filter(plan => 
    ['pending', 'in_progress', 'draft', 'sent'].includes(plan.status)
  );
  
  const completedPlans = plans.filter(plan => 
    ['completed', 'rejected'].includes(plan.status)
  );

  if (isLoading) {
    return <p className="text-muted-foreground">Loading treatment plans...</p>;
  }

  if (plans.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No treatment plans found.</p>
      </div>
    );
  }

  return (
    <div>
      <Tabs defaultValue="active">
        <TabsList className="mb-4">
          <TabsTrigger value="active">Active Plans</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="all">All Plans</TabsTrigger>
        </TabsList>
        
        <TabsContent value="active">
          {activePlans.length > 0 ? (
            activePlans.map(renderPlanCard)
          ) : (
            <p className="text-muted-foreground text-center py-4">No active plans.</p>
          )}
        </TabsContent>
        
        <TabsContent value="completed">
          {completedPlans.length > 0 ? (
            completedPlans.map(renderPlanCard)
          ) : (
            <p className="text-muted-foreground text-center py-4">No completed plans.</p>
          )}
        </TabsContent>
        
        <TabsContent value="all">
          {plans.map(renderPlanCard)}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PatientTreatmentPlans;
